import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  FetchPaginatedApiModel,
  WithFabObj,
  WithRowOperations,
} from "../../../models/shared/CommonModels";
import DeleteDialog from "../../shared/DeleteDialog";
import FilteredDataTable from "../../shared/FilteredDataTable";
import { fetchUbicazioneBeneSelectQueryKey, fetchBeniQueryKey } from "../../../utils/QueryClient";
import {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { Checkbox } from "@mui/material";
import { BeneColoreModel } from "../../../models/api/response/bene/BeneColoreModel";
import { castToBackendSort } from "../../../utils/sorting";
import { SortingModel } from "../../../models/shared/SortingModel";
import { BeneUbicazioneModel } from "../../../models/api/response/bene/BeneUbicazioneModel";
import {
  deleteUbicazione,
  getBeneUbicazioni,
  updateUbicazione,
} from "../../../api/bene/ubicazione";
import BeneUbicazioneFormComponent from "./BeneUbicazioneFormComponent";

interface BeneUbicazioneTableComponentProps extends WithFabObj {
  enableCreate: boolean;
}

const BeneUbicazioneTableComponent: React.FC<
  BeneUbicazioneTableComponentProps
> = ({ alterFabVisibility, fabVisible, enableCreate }) => {
  const queryClient = useQueryClient();

  const [editFormOpen, setEditFormOpen] = useState<boolean>(false);
  const toggleEditFormOpen = () => setEditFormOpen(!editFormOpen);

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const toggleDeleteAlert = () => setDeleteAlertOpen(!deleteAlertOpen);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [ubicazioni, setUbicazioni] = useState<
    FetchPaginatedApiModel<BeneUbicazioneModel[]>
  >(undefined as any);
  const [selectedRowElement, setSelectedRowElement] =
    useState<BeneColoreModel>();

  const [filters, setFilters] = useState<GridFilterModel | undefined>();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [errorMessage, setErrorMessage] = useState("");
  const hideError = () => setErrorMessage("");

  // query
  useQuery(
    [
      fetchUbicazioneBeneSelectQueryKey,
      paginationModel.page,
      filters,
      sortBy,
      sortDirection,
    ],
    () => {
      const sor = {} as SortingModel;
      sor.field = sortBy || "id";
      sor.direction = sortDirection || "DESC";
      return getBeneUbicazioni(0, paginationModel.page, sor, filters);
    },
    {
      keepPreviousData: true,
      onSuccess: (getResponse) => {
        setUbicazioni(getResponse.data);
      },
      refetchOnWindowFocus: false,
    }
  );

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteUbicazione(id),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchUbicazioneBeneSelectQueryKey);
      queryClient.invalidateQueries(fetchBeniQueryKey);
    },
    onError: (e: any) => {
      if (e.response.data.message !== undefined)
        setErrorMessage(e.response.data.message);
      else
        setErrorMessage(
          "Errore non previsto, riprovare o richiedere assistenza"
        );
    },
  });

  const updateDefaultMutation = useMutation({
    mutationFn: (params: { id: number; record: BeneUbicazioneModel }) => {
      return updateUbicazione({
        recordID: params.id,
        data: {
          ubicazione_bene: {
            nome: params.record.nome,
            default: params.record.default === 1 ? 0 : 1,
          },
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(fetchUbicazioneBeneSelectQueryKey);
    },
  });

  const editOperations = (
    record: GridRenderCellParams<any, BeneUbicazioneModel>
  ) => {
    setSelectedRowElement(record.value);
    toggleEditFormOpen();
  };

  const deleteOperations = (
    record: GridRenderCellParams<any, BeneUbicazioneModel>
  ) => {
    setSelectedRowElement(record.value);
    toggleDeleteAlert();
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID Ubicazione",
      flex: 1,
    },
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
    },
    {
      field: "default",
      headerName: "Default",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => (
        <Checkbox
          checked={params.row.default === 1}
          onChange={() => updateDefaultMutation.mutate({ id: params.row.id, record: params.row })}
        />
      ),
    },
  ];

  return (
    <>
      <FilteredDataTable<BeneUbicazioneModel, WithRowOperations>
        loading={ubicazioni === undefined}
        editOperations={editOperations}
        deleteOperations={deleteOperations}
        rows={
          ubicazioni?.data !== undefined
            ? ubicazioni.data.map((d) => {
                return {
                  id: d.id,
                  nome: d.nome,
                  default: d.default,
                  actions: d,
                  delete_action: d,
                  hasEdit: true,
                  hasDelete: true,
                };
              })
            : []
        }
        rowCount={ubicazioni?.count}
        onPaginationModelChange={setPaginationModel}
        columns={columns}
        errorMessage={errorMessage}
        hideError={hideError}
        filters={filters}
        sortModel={sortModel}
        onFilterChange={(newFilterModel) => setFilters(newFilterModel)}
        onSortChange={(newSortModel) => {
          newSortModel.forEach((item) => {
            if (item !== undefined) {
              setSortBy(item.field);
              setSortDirection(castToBackendSort(item.sort));
            }
          });

          setSortModel(newSortModel);
        }}
      />

      {/* Create */}
      <BeneUbicazioneFormComponent
        initialData={{} as BeneColoreModel}
        open={enableCreate ? !fabVisible : false}
        toggleOpen={() => {}}
        onClose={() => alterFabVisibility(true)}
      />

      {/* Edit */}
      {selectedRowElement !== undefined && (
        <BeneUbicazioneFormComponent
          initialData={selectedRowElement}
          open={editFormOpen}
          toggleOpen={() => {
            toggleEditFormOpen();
          }}
          onClose={() => {
            setSelectedRowElement(undefined);
            toggleEditFormOpen();
          }}
          editEnabled={true}
        />
      )}

      {/* Delete */}
      <DeleteDialog
        open={deleteAlertOpen}
        onClose={toggleDeleteAlert}
        identifier={selectedRowElement?.id}
        onPositive={() => {
          toggleDeleteAlert();
          if (selectedRowElement?.id) {
            deleteMutation.mutate(selectedRowElement.id);
          }
        }}
        onNegative={toggleDeleteAlert}
      />
    </>
  );
};

export default BeneUbicazioneTableComponent;
